import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL = 30000; // 30 seconds

const MOCK_NOTIFICATIONS = [
  { id: 'n1', type: 'new_proposal',   title: 'New Quote Received',           body: 'Istanbul Fair Solutions submitted a quote for Hannover Messe 2025',  read: false, created_at: new Date(Date.now() - 300000).toISOString() },
  { id: 'n2', type: 'proposal_accepted', title: 'Quote Accepted!',           body: 'Your proposal for Automechanika Frankfurt has been accepted.',       read: false, created_at: new Date(Date.now() - 900000).toISOString() },
  { id: 'n3', type: 'brief_published', title: 'New Brief Available',         body: 'A new brief for MWC Barcelona 2025 matching your profile is open.',  read: true,  created_at: new Date(Date.now() - 3600000).toISOString() },
  { id: 'n4', type: 'proposal_declined', title: 'Quote Declined',            body: 'Your proposal for Gulfood Dubai was not selected this time.',        read: true,  created_at: new Date(Date.now() - 86400000).toISOString() },
];

const NotificationContext = createContext(null);

export const NotificationProvider = ({ children }) => {
  const { token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const intervalRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(res.data.notifications || []);
      setUnread(res.data.unread || 0);
    } catch {
      // Fall back to mock data silently
      setNotifications(MOCK_NOTIFICATIONS);
      setUnread(MOCK_NOTIFICATIONS.filter(n => !n.read).length);
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetchNotifications();
    intervalRef.current = setInterval(fetchNotifications, POLL_INTERVAL);
    return () => clearInterval(intervalRef.current);
  }, [token, fetchNotifications]);

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch { /* ignore */ }
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnread(0);
  };

  const markRead = async (id) => {
    try {
      await axios.patch(`${API}/notifications/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch { /* ignore */ }
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    setUnread(prev => Math.max(0, prev - 1));
  };

  // Allow other parts of the app to add local notifications (optimistic)
  const addLocal = (notif) => {
    const n = { id: `local_${Date.now()}`, read: false, created_at: new Date().toISOString(), ...notif };
    setNotifications(prev => [n, ...prev]);
    setUnread(prev => prev + 1);
  };

  return (
    <NotificationContext.Provider value={{ notifications, unread, loading, fetchNotifications, markAllRead, markRead, addLocal }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be inside NotificationProvider');
  return ctx;
};

export default NotificationContext;
